import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { Reducer } from 'redux';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import { TAppActions } from '../rootDuck';
import { put, takeLatest, call } from 'redux-saga/effects';

import { ActionsUnion, createAction } from '../../utils/action-helper';
import { IServerResponse } from '../../interfaces/server';
import { IUser, IUserEditProps } from '../../interfaces/user';
import { getMe, editMe } from '../../crud/profile.crud';

const CLEAR_ME = 'profile/CLEAR_ME';
const FETCH_REQUEST = 'profile/FETCH_REQUEST';
const FETCH_SUCCESS = 'profile/FETCH_SUCCESS';
const FETCH_FAIL = 'profile/FETCH_FAIL';

const CLEAR_EDIT = 'profile/CLEAR_EDIT';
const EDIT_REQUEST = 'profile/EDIT_REQUEST';
const EDIT_SUCCESS = 'profile/EDIT_SUCCESS';
const EDIT_FAIL = 'profile/EDIT_FAIL';

export interface IInitialState {
  me: IUser | undefined;
  loading: boolean;
  success: boolean;
  error: string | null;

  editLoading: boolean;
  editSuccess: boolean;
  editError: string | null;
}

const initialState: IInitialState = {
  me: undefined,
  loading: false,
  success: false,
  error: null,

  editLoading: false,
  editSuccess: false,
  editError: null,
};

export const reducer: Reducer<IInitialState & PersistPartial, TAppActions> = persistReducer(
  { storage, key: 'profile', whitelist: ['user', 'authToken'] },
  (state = initialState, action) => {
    switch (action.type) {
      case CLEAR_ME: {
        return { ...state, me: undefined, loading: false, success: false };
      }

      case FETCH_REQUEST: {
        return {
          ...state,
          loading: true,
          error: null,
          success: false,
        };
      }

      case FETCH_SUCCESS: {
        return { ...state, me: action.payload.data, loading: false, success: true };
      }

      case FETCH_FAIL: {
        return { ...state, loading: false, error: action.payload, success: false };
      }

      case CLEAR_EDIT: {
        return { ...state, editLoading: false, editError: null, editSuccess: false };
      }

      case EDIT_REQUEST: {
        return { ...state, editLoading: true, editError: null, editSuccess: false };
      }

      case EDIT_SUCCESS: {
        return { ...state, editLoading: false, editSuccess: true };
      }

      case EDIT_FAIL: {
        return { ...state, editLoading: false, editSuccess: false, editError: action.payload };
      }
      default:
        return state;
    }
  }
);

export const actions = {
  clearMe: () => createAction(CLEAR_ME),
  fetchRequest: () => createAction(FETCH_REQUEST),
  fetchSuccess: (payload: IServerResponse<IUser>) => createAction(FETCH_SUCCESS, payload),
  fetchFail: (payload: string) => createAction(FETCH_FAIL, payload),

  clearEdit: () => createAction(CLEAR_EDIT),
  editRequest: (payload: IUserEditProps | any) => createAction(EDIT_REQUEST, payload),
  editSuccess: () => createAction(EDIT_SUCCESS),
  editFail: (payload: string) => createAction(EDIT_FAIL, payload),
};

export type TActions = ActionsUnion<typeof actions>;

function* fetchSaga() {
  try {
    const { data }: { data: IServerResponse<IUser> } = yield call(() => getMe());
    yield put(actions.fetchSuccess(data));
  } catch (e) {
    yield put(actions.fetchFail(e?.response?.data?.message || 'Network error'));
  }
}

function* editSaga({ payload }: { payload: IUserEditProps }) {
  try {
    yield call(() => editMe(payload));
    yield put(actions.fetchRequest());
    yield put(actions.editSuccess());
  } catch (e) {
    yield put(actions.editFail(e?.response?.data?.message || 'Network error'));
  }
}

export function* saga() {
  yield takeLatest<ReturnType<typeof actions.fetchRequest>>(FETCH_REQUEST, fetchSaga);
  yield takeLatest<ReturnType<typeof actions.editRequest>>(EDIT_REQUEST, editSaga);
}
